import React, { useState } from 'react';
import { createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { ref, update, serverTimestamp } from 'firebase/database';
import { auth, database } from '../firebase';
import { Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import AuthLayout, { AuthNotice, TERMS_URL, PRIVACY_URL } from './Auth/AuthLayout';
import PasswordField from './Auth/PasswordField';
import { authErrorKey, sendVerification, clearAuthCookie } from '../utils/authFlow';

export default function Signup() {
  const { t } = useTranslation('auth');
  const { language } = useLanguage();
  const { currentUser, emailVerified } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (currentUser && !busy) return <Navigate to={emailVerified ? "/onboarding" : "/verify-email"} replace state={location.state} />;
  const submit = async event => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    let user;
    try {
      ({ user } = await createUserWithEmailAndPassword(auth, email.trim(), password));
    } catch (err) {
      setError(authErrorKey(err)); setBusy(false); return;
    }
    // Account creation cannot be rolled back by a failed profile write or email.
    // The authenticated setup page can recover both without creating another user.
    try {
      await update(ref(database, `companies/${user.uid}`), {
        uid: user.uid, linkedin: 'none', filterSubstageEnabled: true,
        'settings/language': language,
        accountSetup: { version: 1, status: 'incomplete', step: 'company', createdAt: serverTimestamp(), termsAcceptedAt: serverTimestamp() }
      });
    } catch (_) { /* Setup writes the missing profile when the user continues. */ }
    let verificationError = '';
    try { await sendVerification(user, language); }
    catch (err) { verificationError = authErrorKey(err); }
    try {
      await signOut(auth);
      clearAuthCookie();
      navigate('/login', { replace: true, state: { ...location.state, accountCreated: true, email: user.email || email.trim(), verificationError } });
    } catch (err) {
      navigate('/verify-email', { replace: true, state: { ...location.state, verificationError: authErrorKey(err) } });
    }
  };
  return <AuthLayout mode="signup" title={t('signup.title')}
    footer={<>{t('signup.haveAccount')} <Link to="/login" state={location.state}>{t('signup.signInHere')}</Link></>}>
    <AuthNotice>{error && t(error)}</AuthNotice>
    <form aria-labelledby="auth-title" className="auth-form" onSubmit={submit} aria-busy={busy}>
      <div className="auth-field"><label htmlFor="email">{t('signup.email')}</label>
        <input id="email" name="email" type="email" autoComplete="username" autoCapitalize="none" spellCheck="false" dir="ltr"
          value={email} onChange={e => setEmail(e.target.value)} required disabled={busy} placeholder="name@company.com" />
      </div>
      <PasswordField value={password} onChange={e => setPassword(e.target.value)} newPassword disabled={busy} />
      <label className="auth-check"><input type="checkbox" name="terms" required disabled={busy} />
        <span>{t('signup.termsPrefix')} <a href={TERMS_URL} target="_blank" rel="noreferrer">{t('signup.termsOfService')}</a> {t('signup.and')} <a href={PRIVACY_URL} target="_blank" rel="noreferrer">{t('signup.privacyPolicy')}</a>.</span>
      </label>
      <button className="auth-primary" type="submit" disabled={busy}>{t(busy ? 'signup.creating' : 'signup.button')}</button>
    </form>
  </AuthLayout>;
}
